import { useCallback, useEffect } from 'react';

import { di } from 'react-magnetic-di';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { LiveChatSendMessage } from '../../common/utils/extension-components-context/types';
import { AI_WIDGET_STORE_UFO_EXPERIENCES } from '../../controllers/chat-store/experiences';
import { useChatStore } from '../../controllers/chat-store/main';

/**
 * TwilioLiveChatSendMessageProvider
 *
 * Component that exposes a getSendMessage static method for sending messages
 * on a Twilio live chat conversation. Follows the same pattern as
 * TwilioLiveChatCloseProvider so that React hooks can be called within
 * the send handler.
 *
 * The Twilio Conversation object is read from the state store
 * (`twilioConversationsWithMetadata`) instead of a module-level variable,
 * which ensures the correct conversation is always used — even after a
 * page refresh or when reopening a conversation from chat history.
 *
 * Consumers access the send function via the extension components system:
 * useExtensionComponent('liveChatSendMessageProvider')?.getSendMessage()
 *
 * Attachments (CSM-only, behind `csm-omni-channel-live-chat-attachments`):
 *   When `files` is provided, each file is attached to the same Twilio
 *   `Message` via `prepareMessage().addMedia(...)`. The text portion (if
 *   any) becomes the `body`, and a `csmClientMessageId` is stamped into
 *   the message attributes so the realtime `messageAdded` listener can
 *   dedupe the help-seeker's own echo.
 */
export const TwilioLiveChatSendMessageProvider: {
	(): null;
	getSendMessage: () => LiveChatSendMessage | null;
} = () => {
	di(useChatStore);

	const [{ currentConversationId, twilioConversationsWithMetadata }] = useChatStore();

	const twilioConversation =
		twilioConversationsWithMetadata[currentConversationId]?.conversation ?? null;

	const sendLiveChatMessage: LiveChatSendMessage = useCallback(
		async (twilioConversationSid: string, content: string, files?: File[]) => {
			if (!twilioConversation || !twilioConversationSid) {
				return null;
			}
			if (twilioConversation.sid !== twilioConversationSid) {
				throw new Error('No active Twilio conversation');
			}
			// Narrow `files` once and reuse the local — avoids the
			// non-null assertion (`files!`) below the FG guard, which TS
			// cannot otherwise prove because `hasFiles` mixes the FG check
			// with the array check.
			const csmFiles =
				!!files && files.length > 0 && fg('csm-omni-channel-live-chat-attachments') ? files : null;

			// FE observability (BEARS-1036): "Chat (send/receive messages)" success
			// event for the help-seeker outbound message via the Twilio SDK. Keyed
			// by the conversation SID + a per-call uuid suffix so concurrent
			// sends never collide on the same UFO instance ID.
			const ufoExperience =
				AI_WIDGET_STORE_UFO_EXPERIENCES.SEND_TWILIO_LIVE_CHAT_MESSAGE.getInstance(
					// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
					`${twilioConversationSid}:${uuidv4()}`,
				);
			ufoExperience?.start();
			try {
				if (!csmFiles) {
					const result = await twilioConversation.sendMessage?.(content);
					ufoExperience?.success();
					return result;
				}

				// Twilio-native path: build a single Message that carries the body
				// and one or more native MCS attachments. We always stamp a
				// `csmClientMessageId` on attribute-bearing messages so the
				// `messageAdded` listener can dedupe / correlate the echo. We do
				// NOT call `recordRecentCsmClientMessageId` from this code path:
				// the help-seeker tab does not add an optimistic copy for
				// attachment sends (the realtime echo IS the source of truth),
				// so consuming the id here would cause the bubble to be dropped.
				// The recorder is intentionally exposed for any future caller
				// that does add an optimistic copy — see `consumeRecentCsm…` in
				// `live-chat-manager`.
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				const csmClientMessageId = uuidv4();
				const builder = twilioConversation.prepareMessage().setAttributes({ csmClientMessageId });

				if (content && content.length > 0) {
					builder.setBody(content);
				}

				for (const file of csmFiles) {
					builder.addMedia({
						contentType: file.type || 'application/octet-stream',
						filename: file.name,
						media: file,
					});
				}

				const result = await builder.build().send();
				ufoExperience?.success();
				return result;
			} catch (error) {
				ufoExperience?.failure();
				throw error;
			}
		},
		[twilioConversation],
	);

	useEffect(() => {
		// Always expose a callable. When there is no live Twilio
		// conversation the function returns `null` (matching the legacy
		// contract), but consumers do not need to defensively check for
		// `getSendMessage` returning a falsy value at call time.
		TwilioLiveChatSendMessageProvider.getSendMessage = () => sendLiveChatMessage;

		return () => {
			TwilioLiveChatSendMessageProvider.getSendMessage = () => null;
		};
	}, [sendLiveChatMessage, twilioConversation]);

	return null;
};

TwilioLiveChatSendMessageProvider.getSendMessage = () => null;
